import { ReactComponent as CenterAlignIcon } from 'assets/icons/toolbar/textAlign/center_align.svg';
import { ReactComponent as LeftAlignIcon } from 'assets/icons/toolbar/textAlign/left_align.svg';
import { ReactComponent as RightAlignIcon } from 'assets/icons/toolbar/textAlign/right_align.svg';
import { TEXT_ALIGNMENT } from 'constants/fonts';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { generateTranslatedOptions } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * TEXT ALIGNMENT DROPDOWN COMPONENT
 *
 * Renders a dropdown with TEXT_ALIGNMENT options
 */
interface TextAlignmentProps {
  value: TEXT_ALIGNMENT;
  onChange: (alignment: TEXT_ALIGNMENT) => void;
}

const ALIGNMENT_OPTIONS = [
  {
    value: 'left',
    label: 'textAlign.left',
    icon: <LeftAlignIcon />,
  },
  {
    value: 'center',
    label: 'textAlign.center',
    icon: <CenterAlignIcon />,
  },
  {
    value: 'right',
    label: 'textAlign.right',
    icon: <RightAlignIcon />,
  },
];

const BUTTON_OPTIONS = {
  justifyContent: 'flex-start',
  _active: {
    bg: 'hover.blue',
    '& path': {
      stroke: 'upgrade.blue.700',
    },
    color: 'upgrade.blue.700',
  },
  fontSize: 'sm',
  fontWeight: 'normal',
  color: 'font.500',
  h: 7,
  gap: 1,
  px: 2,
  mt: '4px !important', // Using `!important` to override forced inline styling
};

export const TextAlignment = memo(({ value, onChange }: TextAlignmentProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false });
  const tooltipLabel = t('textAlign.tooltip');
  const selectedIndex = useMemo(() => ALIGNMENT_OPTIONS.findIndex((opt) => opt.value === value), [value]);
  const toolbarIcon = ALIGNMENT_OPTIONS[selectedIndex]?.icon || <LeftAlignIcon />;

  return (
    <DropdownPopover
      onSelect={onChange}
      options={generateTranslatedOptions(t, ALIGNMENT_OPTIONS)}
      label={tooltipLabel}
      selectedIndex={selectedIndex}
      toolbarIcon={toolbarIcon}
      w={117}
      closeOnSelect
      returnFocusOnClose={false}
      buttonOptionProps={BUTTON_OPTIONS}
      useLabelInsteadOfSelectedLabel
    />
  );
});
TextAlignment.displayName = 'TextAlignment';
